import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Gift, X, CreditCard, Smartphone, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/translate";

const QUICK = [100, 500, 1000, 5000, 10000];
const METHODS = [
  { key: "stripe",  icon: CreditCard, label: "Carte" },
  { key: "airtel",  icon: Smartphone, label: "Airtel" },
  { key: "mtn",     icon: Smartphone, label: "MTN" },
  { key: "orange",  icon: Smartphone, label: "Orange" },
  { key: "mobicash",icon: Smartphone, label: "Mobicash" },
];

/** Floating + animated tip notifications during a live. Polls the backend
 *  every 3s. The artist can't tip themselves, so on their own live this
 *  stays a discreet toast instead of the big fly-up animation fans see
 *  (isOwner). */
export function TipOverlay({ liveId, isOwner }) {
  const [items, setItems] = useState([]);
  const [lastSeen, setLastSeen] = useState(new Date().toISOString());

  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/lives/${liveId}/tips`, { params: { since: lastSeen, limit: 20 } });
        if (!mounted || !data.length) return;
        setLastSeen(data[data.length - 1].created_at);
        if (isOwner) {
          data.forEach((t) => toast(`💸 ${t.user_name} a envoyé ${Intl.NumberFormat().format(t.amount)} ${t.currency}`, { duration: 3000 }));
          return;
        }
        // stagger animations
        data.forEach((t, i) => {
          setTimeout(() => {
            setItems((prev) => [...prev, t]);
            setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== t.id)), 4500);
          }, i * 350);
        });
      } catch { /* silent */ }
    };
    const id = setInterval(poll, 3000);
    return () => { mounted = false; clearInterval(id); };
  }, [liveId, lastSeen, isOwner]);

  if (isOwner) return null;
  return (
    <div className="pointer-events-none absolute right-3 bottom-20 z-20 flex flex-col items-end gap-2" data-testid="tip-overlay">
      {items.map((t) => <TipBubble key={t.id} tip={t} />)}
    </div>
  );
}

function TipBubble({ tip }) {
  return (
    <div
      className="tip-fly flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-[#FF0050] to-[#8A2BE2] shadow-[0_10px_30px_rgba(255,0,80,0.45)] border border-white/20"
      style={{ animation: "tipRise 4.4s cubic-bezier(0.2,0.7,0.2,1) forwards" }}
    >
      <Sparkles className="w-4 h-4 text-yellow-300 shrink-0" />
      <div className="text-white text-sm font-semibold whitespace-nowrap">
        <span className="opacity-90">{tip.user_name}</span>
        <span className="mx-1 opacity-50">·</span>
        <span className="text-yellow-200">{Intl.NumberFormat().format(tip.amount)} {tip.currency}</span>
      </div>
      {tip.message && <span className="text-xs text-white/80 max-w-[180px] truncate">{tip.message}</span>}
    </div>
  );
}

/** Big circular tip button + modal */
export function TipButton({ liveId, artistName, isOwner, onSent }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user || isOwner) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="absolute right-3 bottom-3 z-10 w-14 h-14 rounded-full bg-gradient-to-br from-[#FF0050] to-[#8A2BE2] grid place-items-center shadow-[0_10px_30px_rgba(255,0,80,0.55)] active:scale-95 transition-transform"
        data-testid="open-tip-btn"
        aria-label="Send a tip"
      >
        <Gift className="w-6 h-6 text-white" />
      </button>
      {open && <TipModal liveId={liveId} artistName={artistName} onClose={() => setOpen(false)} onSent={onSent} />}
    </>
  );
}

function TipModal({ liveId, artistName, onClose, onSent }) {
  const { t } = useI18n();
  const [amount, setAmount] = useState(500);
  const [method, setMethod] = useState("stripe");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const a = Number(amount);
    if (!a || a < 1 || a > 10000) {
      toast.error(t.tips.invalidAmount);
      return;
    }
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 700)); // mock processing
      const { data } = await api.post(`/lives/${liveId}/tips`, { amount: a, currency: "FCFA", method, message });
      toast.success(t.tips.thankYou);
      onSent && onSent(data);
      onClose();
    } catch (e) {
      toast.error(tr(e.response?.data?.detail || "Could not publish"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-xl grid place-items-center p-4" data-testid="tip-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 anim-in relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="close" data-testid="tip-close">
          <X className="w-4 h-4 text-zinc-400" />
        </button>
        <h3 className="text-2xl font-black font-display text-white">{t.tips.title}</h3>
        <p className="text-sm text-zinc-400">{t.tips.sub} <span className="text-white font-semibold">{artistName}</span></p>
        <p className="text-[11px] text-amber-400/90 mt-1">{t.tips.demoNotice}</p>

        <div className="mt-5">
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-black font-display text-white tabular-nums">{Intl.NumberFormat().format(amount)}</span>
            <span className="text-zinc-400 font-semibold">FCFA</span>
          </div>
          <input
            type="range" min="1" max="10000" step="1"
            value={amount} onChange={(e) => setAmount(parseInt(e.target.value, 10))}
            className="w-full mt-3 accent-[#FF0050]"
            data-testid="tip-amount-range"
          />
          <div className="grid grid-cols-5 gap-1.5 mt-3">
            {QUICK.map((q) => (
              <button
                key={q} type="button"
                onClick={() => setAmount(q)}
                className={`py-2 rounded-xl text-xs font-bold transition ${amount === q ? "bg-[#FF0050] text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}
                data-testid={`tip-quick-${q}`}
              >{Intl.NumberFormat().format(q)}</button>
            ))}
          </div>
        </div>

        <p className="mt-5 text-xs uppercase tracking-wider text-zinc-500 font-bold">{t.purchase.method}</p>
        <div className="mt-2 grid grid-cols-5 gap-1.5">
          {METHODS.map((m) => (
            <button
              key={m.key} type="button"
              onClick={() => setMethod(m.key)}
              className={`py-2 rounded-xl text-[11px] font-semibold transition flex flex-col items-center gap-1 ${method === m.key ? "bg-[#00F2FE]/10 text-[#00F2FE] border border-[#00F2FE]/40" : "bg-zinc-800 text-zinc-300 border border-transparent hover:border-white/10"}`}
              data-testid={`tip-method-${m.key}`}
            >
              <m.icon className="w-4 h-4" />
              {m.label}
            </button>
          ))}
        </div>

        <input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t.tips.messagePh}
          className="input-base mt-4"
          maxLength={140}
          data-testid="tip-message-input"
        />

        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="tip-send-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
          {t.tips.send} {Intl.NumberFormat().format(amount)} FCFA
        </button>
      </form>
    </div>
  );
}
